import {
  createContext,
  useState,
  useEffect,
  ReactNode,
  useContext,
  FC,
} from "react";
import { useHistory } from "react-router-dom";
import { api, ApiError } from "../api/apiClient";
import {
  SYSTEM_ROLE_CODES,
  TENANT_ROLE_CODES,
} from "../constants/constants";

/* ============================
   Types
   ============================ */

interface TenantUserInfo {
  tenantId: number;
  tenantName: string;
  roles: string[];
}

interface User {
  userId: string;
  email: string;
  displayName: string;
  roles: string[];
  tenants: TenantUserInfo[];
  token: string;
  activeTenantId?: number | null;
  [key: string]: any;
}

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  loading: boolean;

  login: (
    email: string,
    password: string,
  ) => Promise<{ success: boolean; user?: User | null; error?: string }>;
  logout: () => Promise<void>;
  switchTenant: (tenantId: number) => Promise<boolean>;

  systemRoles: string[];
  tenantRoles: string[];

  isSystemAdmin: boolean;
  isTenantAdmin: boolean;
  isServiceManager: boolean;
  canSendCommunications: boolean;
  canAccessAdmin: boolean;

  hasSystemRole: (role: string) => boolean;
  hasTenantRole: (role: string) => boolean;
  selectedTenant: TenantUserInfo | null;
}

/* ============================
   Context
   ============================ */

export const AuthContext = createContext<AuthContextType | undefined>(
  undefined,
);

interface AuthProviderProps {
  children: ReactNode;
}

/* ============================
   Provider
   ============================ */

export const AuthProvider: FC<AuthProviderProps> = ({ children }) => {
  const history = useHistory();

  const [user, setUser] = useState<User | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(true);

  const [systemRoles, setSystemRoles] = useState<string[]>([]);
  const [tenantRoles, setTenantRoles] = useState<string[]>([]);

  /* ============================
       Derived helpers
       ============================ */

  const hasSystemRole = (role: string) =>
    systemRoles.some(
      (systemRole) => systemRole.toLowerCase() === role.toLowerCase(),
    );
  const hasTenantRole = (role: string) =>
    tenantRoles.some(
      (tenantRole) => tenantRole.toLowerCase() === role.toLowerCase(),
    );

  const isSystemAdmin = hasSystemRole(SYSTEM_ROLE_CODES.SYSTEM_ADMIN);
  const isTenantAdmin = hasTenantRole(TENANT_ROLE_CODES.TENANT_ADMIN);
  const isServiceManager = hasTenantRole(TENANT_ROLE_CODES.SERVICE_MANAGER);
  const canSendCommunications =
    isSystemAdmin ||
    isTenantAdmin ||
    (isServiceManager && hasTenantRole(TENANT_ROLE_CODES.COMMUNICATIONS_SENDER));

  const canAccessAdmin = isSystemAdmin || isTenantAdmin;
  const selectedTenant =
    user?.tenants?.find((tenant) => tenant.tenantId === user.activeTenantId) ??
    user?.tenants?.[0] ??
    null;

  const applyAuthenticatedUser = (authenticatedUser: User) => {
    const activeTenant =
      authenticatedUser.tenants?.find(
        (tenant) => tenant.tenantId === authenticatedUser.activeTenantId,
      ) ?? authenticatedUser.tenants?.[0] ?? null;

    setUser({
      ...authenticatedUser,
      activeTenantId: activeTenant?.tenantId ?? null,
    });
    setIsAuthenticated(true);
    setSystemRoles(authenticatedUser.roles ?? []);
    setTenantRoles(activeTenant?.roles ?? []);
  };

  /* ============================
       Check auth on load
       ============================ */

  useEffect(() => {
    const checkAuthStatus = async () => {
      try {
        const data = await api<{ user: User }>("/api/auth/get-user-info", {
          method: "GET",
          auth: "cookie",
          suppressLoading: true,
          authCheck: true,
        });

        const userFromApi: User | null = data?.user ?? null;

        if (userFromApi) {
          applyAuthenticatedUser(userFromApi);
        } else {
          resetAuthState();
        }
      } catch {
        resetAuthState();
      } finally {
        setLoading(false);
      }
    };

    checkAuthStatus();
  }, []);

  const resetAuthState = () => {
    setUser(null);
    setIsAuthenticated(false);
    setSystemRoles([]);
    setTenantRoles([]);
  };

  /* ============================
       Login
       ============================ */

  const login = async (
    email: string,
    password: string,
  ): Promise<{ success: boolean; user?: User | null; error?: string }> => {
    try {
      const data = await api<User>("/api/auth/login", {
        method: "POST",
        body: { email, password },
        auth: "cookie",
      });

      const userFromApi: User | null = data?.token ? data : null;

      if (!userFromApi) {
        return { success: false, error: "Invalid login response." };
      }

      applyAuthenticatedUser(userFromApi);

      // Fire initialise app in background (non-blocking)
      api("/api/initialiseapp", {
        method: "POST",
        auth: "cookie",
        suppressLoading: true,
      }).catch(() => {
        // Never allow initialise failure to affect login
      });

      return { success: true, user: userFromApi };
    } catch (err) {
      if (err instanceof ApiError) {
        const body = err.body as any;

        const errorMessage =
          body?.message ||
          body?.error ||
          (typeof body === "string" ? body : null) ||
          (err.status === 401
            ? "Login failed. Please check your credentials."
            : `Login failed (HTTP ${err.status}).`);

        return { success: false, error: errorMessage };
      }

      console.error("Unexpected login error:", err);
      return {
        success: false,
        error: "An unexpected error occurred. Please try again.",
      };
    }
  };

  const switchTenant = async (tenantId: number): Promise<boolean> => {
    if (selectedTenant?.tenantId === tenantId) return true;

    try {
      const switchedUser = await api<User>(`/api/auth/active-tenant/${tenantId}`, {
        method: "POST",
        auth: "cookie",
      });
      applyAuthenticatedUser(switchedUser);
      sessionStorage.removeItem("frontline_static_data");
      window.location.assign("/dashboard");
      return true;
    } catch (error) {
      console.error("Failed to switch tenant", error);
      return false;
    }
  };

  /* ============================
       Logout
       ============================ */

  const logout = async (): Promise<void> => {
    try {
      await api<void>("/api/auth/logout", {
        method: "POST",
        auth: "cookie",
      });
    } catch (error) {
      console.warn(
        "Logout API call failed, clearing local auth state anyway",
        error,
      );
    } finally {
      // Clear static session cache
      sessionStorage.removeItem("frontline_static_data");

      resetAuthState();
      history.push("/login");
    }
  };

  /* ============================
       Provider
       ============================ */

  return (
    <AuthContext.Provider
      value={{
        user,
        isAuthenticated,
        loading,

        login,
        logout,
        switchTenant,

        systemRoles,
        tenantRoles,

        isSystemAdmin,
        isTenantAdmin,
        isServiceManager,
        canSendCommunications,
        canAccessAdmin,

        hasSystemRole,
        hasTenantRole,

        selectedTenant,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

/* ============================
   Hook
   ============================ */

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}

export default AuthProvider;
